/**
 * Fee HTTP request schemas — SCRUM-213 (DS-012 Taxes & Fees BC §6, §6.10).
 *
 * Wire source of truth for the operator-facing Fee CRUD endpoints. Shapes
 * mirror the SCRUM-209 `Fee` aggregate input: `value` is a
 * `Fixed | Percentage | Compound` discriminated union (§6.2), `assignment` is a
 * `SystemWide | Property | SourceChannel` union (§6.8; deferred kinds rejected),
 * `percentageBasis` is the 5-value enum (§6.5).
 *
 * Validated HERE (structural, mirrors the VO so a malformed body is 422 not 500):
 *   - XOR on `value.kind` (discriminated union + `.strict()`)
 *   - `basisFeeAllowlist` ↔ `RoomPlusSpecificFees` consistency
 *   - non-empty `propertyIds` / `channelIds`
 * Left to the use cases (cross-aggregate / tier — accepted structurally here):
 *   - `RoomPlusSpecificFees` PRO tier gate, GRV scope-intersection,
 *     `OwnerRevenue` / `Taxable`-on-GRV write-rejections, `appliesPer × type`.
 */
import { z } from 'zod';
import { APPLIES_PER_VALUES, COMPOUND_CADENCE_VALUES, COMPOUND_UNIT_VALUES, FEE_RESPONSIBILITY_VALUES, FEE_TEXT_MAX_LENGTH, PERCENTAGE_BASIS_VALUES, REVENUE_DISTRIBUTION_METHOD_VALUES, TAXABLE_STATUS_VALUES, } from './enums.js';
import { boundedText } from './_text.js';
const feeText = boundedText(FEE_TEXT_MAX_LENGTH);
const idList = z.array(z.string().trim().min(1));
/** `value` — Fixed XOR Percentage XOR Compound (mirrors the aggregate `FeeValue`). */
const feeValueSchema = z
    .discriminatedUnion('kind', [
    z.object({ kind: z.literal('Fixed'), valueCents: z.number().int().min(0) }).strict(),
    z
        .object({
        kind: z.literal('Percentage'),
        percentBasisPoints: z.number().int().min(0).max(10000),
        percentageBasis: z.enum(PERCENTAGE_BASIS_VALUES),
        basisFeeAllowlist: idList.nullable(),
    })
        .strict(),
    z
        .object({
        kind: z.literal('Compound'),
        structure: z
            .object({
            unit: z.enum(COMPOUND_UNIT_VALUES),
            cadence: z.enum(COMPOUND_CADENCE_VALUES),
            unitRateCents: z.number().int().min(0),
        })
            .strict(),
    })
        .strict(),
])
    // §6.5: `basisFeeAllowlist` is non-null + non-empty IFF the basis is
    // `RoomPlusSpecificFees`; null for every other basis. Enforced as a
    // superRefine (a `.refine` would turn each branch into a ZodEffects and break
    // the discriminated union).
    .superRefine((v, ctx) => {
    if (v.kind !== 'Percentage')
        return;
    const isSpecific = v.percentageBasis === 'RoomPlusSpecificFees';
    const hasEntries = v.basisFeeAllowlist !== null && v.basisFeeAllowlist.length > 0;
    if (isSpecific && !hasEntries) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['basisFeeAllowlist'],
            message: 'RoomPlusSpecificFees requires a non-empty basisFeeAllowlist',
        });
    }
    if (!isSpecific && v.basisFeeAllowlist !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['basisFeeAllowlist'],
            message: 'basisFeeAllowlist must be null unless percentageBasis is RoomPlusSpecificFees',
        });
    }
});
/** `assignment` — Phase-4 scopes; deferred kinds rejected (§6.8). */
const feeAssignmentSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('SystemWide') }).strict(),
    z.object({ kind: z.literal('Property'), propertyIds: idList.min(1) }).strict(),
    z.object({ kind: z.literal('SourceChannel'), channelIds: idList.min(1) }).strict(),
]);
export const createFeeSchema = z
    .object({
    name: feeText,
    guestFacingName: feeText,
    value: feeValueSchema,
    appliesPer: z.enum(APPLIES_PER_VALUES),
    revenueCategory: feeText,
    taxableStatus: z.enum(TAXABLE_STATUS_VALUES),
    distributionEligible: z.boolean(),
    assignment: feeAssignmentSchema,
    responsibility: z.enum(FEE_RESPONSIBILITY_VALUES),
    distributionMethodOverride: z.enum(REVENUE_DISTRIBUTION_METHOD_VALUES).nullable().optional(),
})
    .strict();
export const updateFeeLabelsSchema = z
    .object({
    name: feeText.optional(),
    guestFacingName: feeText.optional(),
})
    .strict()
    .refine((o) => o.name !== undefined || o.guestFacingName !== undefined, {
    message: 'at_least_one_field',
});
export const updateFeeConfigSchema = z
    .object({
    value: feeValueSchema.optional(),
    appliesPer: z.enum(APPLIES_PER_VALUES).optional(),
    revenueCategory: feeText.optional(),
    taxableStatus: z.enum(TAXABLE_STATUS_VALUES).optional(),
    distributionEligible: z.boolean().optional(),
    assignment: feeAssignmentSchema.optional(),
    responsibility: z.enum(FEE_RESPONSIBILITY_VALUES).optional(),
    distributionMethodOverride: z.enum(REVENUE_DISTRIBUTION_METHOD_VALUES).nullable().optional(),
})
    .strict()
    .refine((o) => Object.keys(o).length > 0, { message: 'at_least_one_field' });
//# sourceMappingURL=fee.schema.js.map